import { useEffect, useState } from 'react';
import { Clock3 } from 'lucide-react';
import './PaymentDeadline.css';

const COPY = {
  en: { title: 'Payment deadline', until: 'Complete payment by', zone: 'Japan time', hours: 'HOURS', minutes: 'MIN', seconds: 'SEC', expired: 'This payment link has expired', contact: 'Please contact us for a new payment link.' },
  ja: { title: 'お支払い期限', until: 'お支払い締切', zone: '日本時間', hours: '時間', minutes: '分', seconds: '秒', expired: 'お支払い期限を過ぎています', contact: '新しいお支払いリンクをご依頼ください。' },
  zh: { title: '支付期限', until: '请于以下时间前完成支付', zone: '日本时间', hours: '小时', minutes: '分钟', seconds: '秒', expired: '支付链接已过期', contact: '请联系我们获取新的支付链接。' },
  zhHant: { title: '付款期限', until: '請於以下時間前完成付款', zone: '日本時間', hours: '小時', minutes: '分鐘', seconds: '秒', expired: '付款連結已過期', contact: '請聯絡我們取得新的付款連結。' },
};

export function deadlineParts(remaining) {
  const seconds = Math.max(0, Math.ceil(remaining / 1000));
  return [Math.floor(seconds / 3600), Math.floor(seconds / 60) % 60, seconds % 60].map(value => String(value).padStart(2, '0'));
}

export function usePaymentDeadline(expiresAt, serverNow) {
  const [remaining, setRemaining] = useState(null);
  useEffect(() => {
    const deadline = Date.parse(expiresAt);
    if (!Number.isFinite(deadline)) { setRemaining(null); return undefined; }
    const parsedServerNow = Date.parse(serverNow);
    const startedAt = Number.isFinite(parsedServerNow) ? parsedServerNow : Date.now();
    const startedMonotonic = performance.now();
    // A monotonic clock keeps the server-based countdown independent of device clock changes.
    const tick = () => setRemaining(Math.max(0, deadline - startedAt - (performance.now() - startedMonotonic)));
    tick();
    const interval = setInterval(tick, 1000);
    document.addEventListener('visibilitychange', tick);
    window.addEventListener('focus', tick);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
      window.removeEventListener('focus', tick);
    };
  }, [expiresAt, serverNow]);
  return { remaining, expired: remaining === 0 };
}

export default function PaymentDeadline({ expiresAt, remaining, language = 'en' }) {
  const deadline = Date.parse(expiresAt);
  if (!Number.isFinite(deadline)) return null;
  const copy = COPY[language] || COPY.en;
  const expired = remaining === 0;
  const urgent = remaining !== null && remaining <= 3600000;
  const locale = { en: 'en-GB', ja: 'ja-JP', zh: 'zh-CN', zhHant: 'zh-TW' }[language] || 'en-GB';
  const date = new Intl.DateTimeFormat(locale, { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(deadline));
  const parts = remaining === null ? ['--', '--', '--'] : deadlineParts(remaining);
  return <section className={`payment-deadline ${urgent ? 'is-urgent' : ''}`} aria-label={copy.title} data-testid="payment-deadline">
    <div className="payment-deadline-copy">
      <div className="payment-deadline-title"><Clock3 size={18} aria-hidden="true" /><h2>{expired ? copy.expired : copy.title}</h2></div>
      <p>{expired ? copy.contact : copy.until}</p>
      <div className="payment-deadline-date"><time dateTime={expiresAt}>{date}</time><span>{copy.zone}</span></div>
    </div>
    <div className="payment-deadline-timer" role="timer" aria-live="off" aria-label={copy.title}>
      {parts.map((part, index) => <div className="payment-deadline-unit" key={index}><strong>{part}</strong><span>{[copy.hours, copy.minutes, copy.seconds][index]}</span></div>)}
    </div>
  </section>;
}
